import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  CanceledError,
  InternalAxiosRequestConfig,
} from 'axios';
import { getAuth, User } from 'firebase/auth';
import { store } from 'src/redux/store';

export const publicAxiosBaseUrl = process.env.REACT_APP_PUBLIC_API_URL;
export const axiosBaseUrl = process.env.REACT_APP_API_URL;

export const axiosConfig: AxiosRequestConfig = {
  baseURL: axiosBaseUrl,
};

export const axiosBackendClient = axios.create(axiosConfig);

interface TokenRetryConfig extends InternalAxiosRequestConfig {
  authUid?: string | null;
  tokenRefreshed?: boolean;
}

const forcedTokenRefreshes = new Map<string, Promise<string>>();

const forceTokenRefresh = (user: User): Promise<string> => {
  const pending = forcedTokenRefreshes.get(user.uid);
  if (pending) return pending;

  const refresh = user.getIdToken(true).finally(() => {
    forcedTokenRefreshes.delete(user.uid);
  });
  forcedTokenRefreshes.set(user.uid, refresh);
  return refresh;
};

const assertCurrentAccount = (uid: string | null): void => {
  if ((getAuth().currentUser?.uid ?? null) !== uid) {
    throw new CanceledError('Żądanie anulowane po zmianie konta.');
  }
};

// FirebaseAuthMiddleware rejects before any handler runs, so a replay is safe.
const isTokenRejected = (error: AxiosError): boolean =>
  error.response?.status === 403 &&
  (error.response.data as { message?: unknown } | undefined)?.message ===
    'access denied';

axiosBackendClient.interceptors.request.use(async function (
  config: TokenRetryConfig,
): Promise<TokenRetryConfig> {
  // Only set Content-Type to application/json if it's not already set
  // This allows multipart/form-data for file uploads
  if (!config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json';
  }
  config.headers['Accept'] = 'application/json';

  const user = getAuth().currentUser;
  // Preserve the initiating account across retries and asynchronous token reads.
  if (config.authUid === undefined) config.authUid = user?.uid ?? null;
  assertCurrentAccount(config.authUid);

  // Cached: the SDK refreshes near expiry. Forcing it on every request
  // exhausts the Firebase securetoken quota (#792); authority comes from the DB.
  const token = await user?.getIdToken();
  assertCurrentAccount(config.authUid);
  if (token) {
    config.headers.Authorization = token;
  }

  // Add the selected organization unless the request names one itself. It is
  // read after the async token read above, so callers that must not follow
  // a switch made in between (the Magazyn section) send it explicitly.
  const state = store.getState();
  const selectedOrganization = state.organization.selectedOrganization;
  if (selectedOrganization?.id && !config.headers.has('x-organization-id')) {
    config.headers['x-organization-id'] = selectedOrganization.id;
  }

  return config;
});

axiosBackendClient.interceptors.response.use(
  undefined,
  async (error: AxiosError): Promise<AxiosResponse> => {
    const config = error.config as TokenRetryConfig | undefined;
    const user = getAuth().currentUser;
    if (
      !config ||
      config.tokenRefreshed ||
      !user ||
      config.authUid !== user.uid ||
      !isTokenRejected(error)
    ) {
      throw error;
    }
    config.tokenRefreshed = true;
    const token = await user.getIdToken();
    assertCurrentAccount(user.uid);
    // A delayed rejection may refer to a token another request already replaced.
    if (token === config.headers.Authorization) await forceTokenRefresh(user);
    return axiosBackendClient(config);
  },
);

export default axiosBackendClient;
